import { supabase } from '@/shared/services/supabase';

/**
 * Reports Service
 * Handles all reporting queries with SQL aggregation
 */

// ============================================================================
// Database Query Result Types
// ============================================================================

interface FrameReplenishmentQueryResult {
  id: string;
  location_id: string;
  manufacturer_id: string;
  status: string;
  ordered_date?: string | null;
  received_date?: string | null;
  reorder_entry_date: string;
  order_type: string;
  brand?: string;
  frame_name_or_model?: string;
  color?: string;
  eye_size?: string;
  patient_name?: string | null;
  tray_number?: string | null;
  po_number?: string | null;
  location?: {
    id: string;
    name: string;
    short_code?: string;
  };
  manufacturer?: {
    id: string;
    name: string;
  };
}

// ============================================================================
// Location Summary Report Types
// ============================================================================

export interface LocationSummaryData {
  location_id: string;
  location_name: string;
  location_short_code?: string;
  frames_added: number;
  frames_ordered: number;
  frames_received: number;
  stock_orders: number;
  patient_orders: number;
  days_with_activity: number;
}

export interface LocationSummaryFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
}

// ============================================================================
// Manufacturer Performance Report Types
// ============================================================================

export interface ManufacturerPerformanceData {
  manufacturer_id: string;
  manufacturer_name: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  frames_ordered: number;
  frames_received: number;
  avg_days_to_receive: number;
  oldest_pending_days: number | null;
}

export interface ManufacturerPerformanceFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
  manufacturer_ids?: string[];
}

// ============================================================================
// Aging Exceptions Report Types
// ============================================================================

export interface AgingExceptionItem {
  id: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  manufacturer_id: string;
  manufacturer_name: string;
  brand: string;
  frame_name_or_model: string;
  color: string;
  eye_size: string;
  order_type: 'STOCK' | 'PATIENT';
  patient_name: string | null;
  tray_number: string | null;
  status: 'PENDING' | 'ORDERED';
  reorder_entry_date: string;
  ordered_date: string | null;
  days_aging: number;
  po_number: string | null;
}

export interface AgingExceptionsFilters {
  location_ids?: string[];
  manufacturer_ids?: string[];
  status?: 'PENDING' | 'ORDERED' | 'BOTH';
  min_days?: number;
}

// ============================================================================
// Usage Compliance Report Types
// ============================================================================

export interface UsageComplianceData {
  location_id: string;
  location_name: string;
  location_short_code?: string;
  date_range_days: number;
  days_with_entries: number;
  total_entries: number;
  entries_per_active_day: number;
  usage_percentage: number;
}

export interface UsageComplianceFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
}

// ============================================================================
// Daily Receiving Report Types
// ============================================================================

export interface DailyReceivingData {
  id: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  manufacturer_id: string;
  manufacturer_name: string;
  brand: string;
  frame_name_or_model: string;
  color: string;
  eye_size: string;
  order_type: 'STOCK' | 'PATIENT';
  patient_name: string | null;
  tray_number: string | null;
  received_date: string;
  ordered_date: string | null;
  po_number: string | null;
  days_to_receive: number | null;
}

export interface DailyReceivingFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
  manufacturer_ids?: string[];
}

// ============================================================================
// Patient Order Tracking Report Types
// ============================================================================

export interface PatientOrderTrackingData {
  id: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  manufacturer_id: string;
  manufacturer_name: string;
  brand: string;
  frame_name_or_model: string;
  color: string;
  eye_size: string;
  patient_name: string;
  tray_number: string;
  status: 'PENDING' | 'ORDERED' | 'RECEIVED';
  reorder_entry_date: string;
  ordered_date: string | null;
  received_date: string | null;
  po_number: string | null;
  days_since_entry: number;
  days_since_ordered: number | null;
}

export interface PatientOrderTrackingFilters {
  location_ids?: string[];
  patient_name?: string;
  status?: ('PENDING' | 'ORDERED' | 'RECEIVED')[];
  date_from?: string;
  date_to?: string;
}

// ============================================================================
// Frame Turnaround Report Types
// ============================================================================

export interface FrameTurnaroundData {
  manufacturer_id: string;
  manufacturer_name: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  order_type: 'STOCK' | 'PATIENT' | 'ALL';
  avg_days_entry_to_ordered: number | null;
  avg_days_ordered_to_received: number | null;
  avg_total_days: number | null;
  sample_size: number;
}

export interface FrameTurnaroundFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
  manufacturer_ids?: string[];
  order_type?: 'STOCK' | 'PATIENT' | 'ALL';
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Count working days (Monday-Friday) in a date range
 * Excludes weekends (Saturday, Sunday)
 */
function countWorkingDays(startDate: Date, endDate: Date): number {
  let workingDays = 0;
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workingDays;
}

/**
 * Check if a date is a working day (Monday-Friday)
 */
function isWorkingDay(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek !== 0 && dayOfWeek !== 6;
}

// ============================================================================
// Service Implementation
// ============================================================================

export const reportsService = {
  /**
   * Get location summary report
   * Shows high-level metrics per location for date range
   */
  async getLocationSummary(
    filters: LocationSummaryFilters
  ): Promise<LocationSummaryData[]> {
    const { date_from, date_to, location_ids } = filters;

    // Build query for frames within date range
    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        status,
        order_type,
        reorder_entry_date,
        ordered_date,
        received_date,
        location:locations!inner(id, name, short_code)
      `
      )
      .gte('reorder_entry_date', date_from)
      .lte('reorder_entry_date', date_to);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching location summary:', error);
      throw new Error(error.message);
    }

    // Aggregate data by location
    const locationMap = new Map<string, LocationSummaryData>();

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      const locationId = item.location_id;
      const locationName = item.location?.name || 'Unknown';
      const locationShortCode = item.location?.short_code;

      if (!locationMap.has(locationId)) {
        locationMap.set(locationId, {
          location_id: locationId,
          location_name: locationName,
          location_short_code: locationShortCode,
          frames_added: 0,
          frames_ordered: 0,
          frames_received: 0,
          stock_orders: 0,
          patient_orders: 0,
          days_with_activity: 0,
        });
      }

      const summary = locationMap.get(locationId)!;

      // Count frames added
      summary.frames_added++;

      // Count by status
      if (item.status === 'ORDERED' || item.status === 'RECEIVED') {
        summary.frames_ordered++;
      }
      if (item.status === 'RECEIVED') {
        summary.frames_received++;
      }

      // Count by order type
      if (item.order_type === 'STOCK') {
        summary.stock_orders++;
      } else {
        summary.patient_orders++;
      }
    });

    // Calculate days with activity
    const activityByLocation = new Map<string, Set<string>>();
    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      const locationId = item.location_id;
      if (!activityByLocation.has(locationId)) {
        activityByLocation.set(locationId, new Set());
      }
      activityByLocation.get(locationId)!.add(item.reorder_entry_date);
    });

    activityByLocation.forEach((dates, locationId) => {
      const summary = locationMap.get(locationId);
      if (summary) {
        summary.days_with_activity = dates.size;
      }
    });

    return Array.from(locationMap.values()).sort((a, b) =>
      a.location_name.localeCompare(b.location_name)
    );
  },

  /**
   * Get manufacturer performance report
   * Shows ordering and receiving metrics per manufacturer and location
   */
  async getManufacturerPerformance(
    filters: ManufacturerPerformanceFilters
  ): Promise<ManufacturerPerformanceData[]> {
    const { date_from, date_to, location_ids, manufacturer_ids } = filters;

    // Query for ordered/received frames
    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        status,
        ordered_date,
        received_date,
        reorder_entry_date,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name)
      `
      )
      .gte('reorder_entry_date', date_from)
      .lte('reorder_entry_date', date_to)
      .in('status', ['ORDERED', 'RECEIVED']);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (manufacturer_ids && manufacturer_ids.length > 0) {
      query = query.in('manufacturer_id', manufacturer_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching manufacturer performance:', error);
      throw new Error(error.message);
    }

    // Aggregate by manufacturer + location
    const perfMap = new Map<string, ManufacturerPerformanceData>();

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      const key = `${item.manufacturer_id}-${item.location_id}`;
      const manufacturerName = item.manufacturer?.name || 'Unknown';
      const locationName = item.location?.name || 'Unknown';
      const locationShortCode = item.location?.short_code;

      if (!perfMap.has(key)) {
        perfMap.set(key, {
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: manufacturerName,
          location_id: item.location_id,
          location_name: locationName,
          location_short_code: locationShortCode,
          frames_ordered: 0,
          frames_received: 0,
          avg_days_to_receive: 0,
          oldest_pending_days: null,
        });
      }

      const perf = perfMap.get(key)!;
      perf.frames_ordered++;

      if (item.status === 'RECEIVED') {
        perf.frames_received++;
      }
    });

    // Calculate average days to receive
    const daysMap = new Map<string, number[]>();
    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      if (item.status === 'RECEIVED' && item.ordered_date && item.received_date) {
        const key = `${item.manufacturer_id}-${item.location_id}`;
        if (!daysMap.has(key)) {
          daysMap.set(key, []);
        }
        const days = Math.floor(
          (new Date(item.received_date).getTime() -
            new Date(item.ordered_date).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        daysMap.get(key)!.push(days);
      }
    });

    daysMap.forEach((daysList, key) => {
      const perf = perfMap.get(key);
      if (perf && daysList.length > 0) {
        const avg = daysList.reduce((sum, d) => sum + d, 0) / daysList.length;
        perf.avg_days_to_receive = Math.round(avg);
      }
    });

    return Array.from(perfMap.values()).sort(
      (a, b) =>
        b.frames_ordered - a.frames_ordered ||
        a.manufacturer_name.localeCompare(b.manufacturer_name)
    );
  },

  /**
   * Get aging exceptions report
   * Shows frames that have been PENDING or ORDERED for too long
   */
  async getAgingExceptions(
    filters: AgingExceptionsFilters
  ): Promise<AgingExceptionItem[]> {
    const { location_ids, manufacturer_ids, status, min_days = 0 } = filters;

    // Determine which statuses to query
    const statusFilter =
      status === 'PENDING'
        ? ['PENDING']
        : status === 'ORDERED'
        ? ['ORDERED']
        : ['PENDING', 'ORDERED'];

    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        brand,
        frame_name_or_model,
        color,
        eye_size,
        order_type,
        patient_name,
        tray_number,
        status,
        reorder_entry_date,
        ordered_date,
        po_number,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name)
      `
      )
      .in('status', statusFilter);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (manufacturer_ids && manufacturer_ids.length > 0) {
      query = query.in('manufacturer_id', manufacturer_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching aging exceptions:', error);
      throw new Error(error.message);
    }

    const today = new Date();
    const results: AgingExceptionItem[] = [];

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      // Calculate aging based on status
      let referenceDate = item.reorder_entry_date;
      if (item.status === 'ORDERED' && item.ordered_date) {
        referenceDate = item.ordered_date;
      }

      const refDate = new Date(referenceDate);
      const daysAging = Math.floor(
        (today.getTime() - refDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Filter by minimum days
      if (daysAging >= min_days) {
        results.push({
          id: item.id,
          location_id: item.location_id,
          location_name: item.location?.name || 'Unknown',
          location_short_code: item.location?.short_code,
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: item.manufacturer?.name || 'Unknown',
          brand: item.brand || '',
          frame_name_or_model: item.frame_name_or_model || '',
          color: item.color || '',
          eye_size: item.eye_size || '',
          order_type: item.order_type as 'STOCK' | 'PATIENT',
          patient_name: item.patient_name || null,
          tray_number: item.tray_number || null,
          status: item.status as 'PENDING' | 'ORDERED',
          reorder_entry_date: item.reorder_entry_date,
          ordered_date: item.ordered_date || null,
          days_aging: daysAging,
          po_number: item.po_number || null,
        });
      }
    });

    // Sort by days_aging descending (worst first)
    return results.sort((a, b) => b.days_aging - a.days_aging);
  },

  /**
   * Get usage compliance report
   * Shows system adoption and activity levels per location
   *
   * Usage % = Percentage of working days (Monday-Friday) that had at least one entry
   */
  async getUsageCompliance(
    filters: UsageComplianceFilters
  ): Promise<UsageComplianceData[]> {
    const { date_from, date_to, location_ids } = filters;

    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        reorder_entry_date,
        location:locations!inner(id, name, short_code)
      `
      )
      .gte('reorder_entry_date', date_from)
      .lte('reorder_entry_date', date_to);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching usage compliance:', error);
      throw new Error(error.message);
    }

    // Calculate working days (Monday-Friday only) in the date range
    const dateFrom = new Date(date_from);
    const dateTo = new Date(date_to);
    const workingDaysInRange = countWorkingDays(dateFrom, dateTo);

    // Aggregate by location
    const usageMap = new Map<string, UsageComplianceData>();

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      const locationId = item.location_id;
      const locationName = item.location?.name || 'Unknown';
      const locationShortCode = item.location?.short_code;

      if (!usageMap.has(locationId)) {
        usageMap.set(locationId, {
          location_id: locationId,
          location_name: locationName,
          location_short_code: locationShortCode,
          date_range_days: workingDaysInRange,
          days_with_entries: 0,
          total_entries: 0,
          entries_per_active_day: 0,
          usage_percentage: 0,
        });
      }

      const usage = usageMap.get(locationId)!;
      usage.total_entries++;
    });

    // Calculate unique working days with entries (only count Monday-Friday)
    const daysMap = new Map<string, Set<string>>();
    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      const locationId = item.location_id;
      const entryDate = new Date(item.reorder_entry_date);

      // Only count entries on working days
      if (isWorkingDay(entryDate)) {
        if (!daysMap.has(locationId)) {
          daysMap.set(locationId, new Set());
        }
        daysMap.get(locationId)!.add(item.reorder_entry_date);
      }
    });

    daysMap.forEach((dates, locationId) => {
      const usage = usageMap.get(locationId);
      if (usage) {
        usage.days_with_entries = dates.size;
        usage.entries_per_active_day =
          usage.total_entries / (usage.days_with_entries || 1); // Avoid division by zero
        usage.usage_percentage = workingDaysInRange > 0
          ? (usage.days_with_entries / workingDaysInRange) * 100
          : 0;
      }
    });

    return Array.from(usageMap.values()).sort((a, b) =>
      a.location_name.localeCompare(b.location_name)
    );
  },

  /**
   * Get daily receiving report
   * Shows frames received in a date range, useful for daily receiving tasks
   */
  async getDailyReceiving(
    filters: DailyReceivingFilters
  ): Promise<DailyReceivingData[]> {
    const { date_from, date_to, location_ids, manufacturer_ids } = filters;

    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        brand,
        frame_name_or_model,
        color,
        eye_size,
        order_type,
        patient_name,
        tray_number,
        received_date,
        ordered_date,
        po_number,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name)
      `
      )
      .eq('status', 'RECEIVED')
      .gte('received_date', date_from)
      .lte('received_date', date_to)
      .order('received_date', { ascending: false })
      .order('location_id', { ascending: true });

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (manufacturer_ids && manufacturer_ids.length > 0) {
      query = query.in('manufacturer_id', manufacturer_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching daily receiving:', error);
      throw new Error(error.message);
    }

    const results: DailyReceivingData[] = (data || []).map(
      (item: FrameReplenishmentQueryResult) => {
        let daysToReceive: number | null = null;
        if (item.ordered_date && item.received_date) {
          daysToReceive = Math.floor(
            (new Date(item.received_date).getTime() -
              new Date(item.ordered_date).getTime()) /
              (1000 * 60 * 60 * 24)
          );
        }

        return {
          id: item.id,
          location_id: item.location_id,
          location_name: item.location?.name || 'Unknown',
          location_short_code: item.location?.short_code,
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: item.manufacturer?.name || 'Unknown',
          brand: item.brand || '',
          frame_name_or_model: item.frame_name_or_model || '',
          color: item.color || '',
          eye_size: item.eye_size || '',
          order_type: item.order_type as 'STOCK' | 'PATIENT',
          patient_name: item.patient_name || null,
          tray_number: item.tray_number || null,
          received_date: item.received_date || '',
          ordered_date: item.ordered_date || null,
          po_number: item.po_number || null,
          days_to_receive: daysToReceive,
        };
      }
    );

    return results;
  },

  /**
   * Get patient order tracking report
   * Shows all patient orders (order_type = 'PATIENT') with current status
   */
  async getPatientOrderTracking(
    filters: PatientOrderTrackingFilters
  ): Promise<PatientOrderTrackingData[]> {
    const {
      location_ids,
      patient_name,
      status,
      date_from,
      date_to,
    } = filters;

    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        brand,
        frame_name_or_model,
        color,
        eye_size,
        patient_name,
        tray_number,
        status,
        reorder_entry_date,
        ordered_date,
        received_date,
        po_number,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name)
      `
      )
      .eq('order_type', 'PATIENT');

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (patient_name) {
      query = query.ilike('patient_name', `%${patient_name}%`);
    }

    if (status && status.length > 0) {
      query = query.in('status', status);
    }

    if (date_from) {
      query = query.gte('reorder_entry_date', date_from);
    }

    if (date_to) {
      query = query.lte('reorder_entry_date', date_to);
    }

    query = query.order('reorder_entry_date', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching patient order tracking:', error);
      throw new Error(error.message);
    }

    const today = new Date();
    const results: PatientOrderTrackingData[] = (data || []).map(
      (item: FrameReplenishmentQueryResult) => {
        const entryDate = new Date(item.reorder_entry_date);
        const daysSinceEntry = Math.floor(
          (today.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        let daysSinceOrdered: number | null = null;
        if (item.ordered_date) {
          const orderedDate = new Date(item.ordered_date);
          daysSinceOrdered = Math.floor(
            (today.getTime() - orderedDate.getTime()) / (1000 * 60 * 60 * 24)
          );
        }

        return {
          id: item.id,
          location_id: item.location_id,
          location_name: item.location?.name || 'Unknown',
          location_short_code: item.location?.short_code,
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: item.manufacturer?.name || 'Unknown',
          brand: item.brand || '',
          frame_name_or_model: item.frame_name_or_model || '',
          color: item.color || '',
          eye_size: item.eye_size || '',
          patient_name: item.patient_name || '',
          tray_number: item.tray_number || '',
          status: item.status as 'PENDING' | 'ORDERED' | 'RECEIVED',
          reorder_entry_date: item.reorder_entry_date,
          ordered_date: item.ordered_date || null,
          received_date: item.received_date || null,
          po_number: item.po_number || null,
          days_since_entry: daysSinceEntry,
          days_since_ordered: daysSinceOrdered,
        };
      }
    );

    return results;
  },

  /**
   * Get frame turnaround report
   * Shows average time from entry → ordered → received
   */
  async getFrameTurnaround(
    filters: FrameTurnaroundFilters
  ): Promise<FrameTurnaroundData[]> {
    const { date_from, date_to, location_ids, manufacturer_ids, order_type } = filters;

    // Query for frames that have been received (need complete cycle)
    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        order_type,
        reorder_entry_date,
        ordered_date,
        received_date,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name)
      `
      )
      .eq('status', 'RECEIVED')
      .not('received_date', 'is', null)
      .not('ordered_date', 'is', null)
      .gte('received_date', date_from)
      .lte('received_date', date_to);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (manufacturer_ids && manufacturer_ids.length > 0) {
      query = query.in('manufacturer_id', manufacturer_ids);
    }

    if (order_type && order_type !== 'ALL') {
      query = query.eq('order_type', order_type);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching frame turnaround:', error);
      throw new Error(error.message);
    }

    // Aggregate by manufacturer + location + order_type
    const turnaroundMap = new Map<string, FrameTurnaroundData>();

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      if (!item.ordered_date || !item.received_date) return;

      const key = `${item.manufacturer_id}-${item.location_id}-${item.order_type}`;
      const manufacturerName = item.manufacturer?.name || 'Unknown';
      const locationName = item.location?.name || 'Unknown';
      const locationShortCode = item.location?.short_code;

      if (!turnaroundMap.has(key)) {
        turnaroundMap.set(key, {
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: manufacturerName,
          location_id: item.location_id,
          location_name: locationName,
          location_short_code: locationShortCode,
          order_type: item.order_type as 'STOCK' | 'PATIENT',
          avg_days_entry_to_ordered: null,
          avg_days_ordered_to_received: null,
          avg_total_days: null,
          sample_size: 0,
        });
      }

      const turnaround = turnaroundMap.get(key)!;
      turnaround.sample_size++;
    });

    // Calculate averages
    const entryToOrderedDays: Record<string, number[]> = {};
    const orderedToReceivedDays: Record<string, number[]> = {};
    const totalDays: Record<string, number[]> = {};

    (data || []).forEach((item: FrameReplenishmentQueryResult) => {
      if (!item.ordered_date || !item.received_date) return;

      const key = `${item.manufacturer_id}-${item.location_id}-${item.order_type}`;

      const entryDate = new Date(item.reorder_entry_date);
      const orderedDate = new Date(item.ordered_date);
      const receivedDate = new Date(item.received_date);

      const daysEntryToOrdered = Math.floor(
        (orderedDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const daysOrderedToReceived = Math.floor(
        (receivedDate.getTime() - orderedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const daysTotal = Math.floor(
        (receivedDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (!entryToOrderedDays[key]) entryToOrderedDays[key] = [];
      if (!orderedToReceivedDays[key]) orderedToReceivedDays[key] = [];
      if (!totalDays[key]) totalDays[key] = [];

      entryToOrderedDays[key].push(daysEntryToOrdered);
      orderedToReceivedDays[key].push(daysOrderedToReceived);
      totalDays[key].push(daysTotal);
    });

    // Calculate averages
    turnaroundMap.forEach((turnaround, key) => {
      if (entryToOrderedDays[key] && entryToOrderedDays[key].length > 0) {
        const avg = entryToOrderedDays[key].reduce((sum, d) => sum + d, 0) / entryToOrderedDays[key].length;
        turnaround.avg_days_entry_to_ordered = Math.round(avg * 10) / 10;
      }

      if (orderedToReceivedDays[key] && orderedToReceivedDays[key].length > 0) {
        const avg = orderedToReceivedDays[key].reduce((sum, d) => sum + d, 0) / orderedToReceivedDays[key].length;
        turnaround.avg_days_ordered_to_received = Math.round(avg * 10) / 10;
      }

      if (totalDays[key] && totalDays[key].length > 0) {
        const avg = totalDays[key].reduce((sum, d) => sum + d, 0) / totalDays[key].length;
        turnaround.avg_total_days = Math.round(avg * 10) / 10;
      }
    });

    return Array.from(turnaroundMap.values()).sort(
      (a, b) =>
        b.sample_size - a.sample_size ||
        a.manufacturer_name.localeCompare(b.manufacturer_name)
    );
  },

  /**
   * Get order efficiency report
   * Analyzes orders to show whether minimum order quantities are being met
   * Note: Free shipping analysis requires order amount data not currently tracked
   */
  async getOrderEfficiency(
    filters: OrderEfficiencyFilters
  ): Promise<OrderEfficiencyData[]> {
    const { date_from, date_to, location_ids, manufacturer_ids } = filters;

    // Query for ordered frames (grouped by PO number to analyze actual orders)
    let query = supabase
      .from('frame_replenishment_items')
      .select(
        `
        id,
        location_id,
        manufacturer_id,
        po_number,
        ordered_date,
        location:locations!inner(id, name, short_code),
        manufacturer:manufacturers!inner(id, name, min_order_qty, min_order_amt, free_ship_amt)
      `
      )
      .not('po_number', 'is', null)
      .not('ordered_date', 'is', null)
      .gte('ordered_date', date_from)
      .lte('ordered_date', date_to)
      .in('status', ['ORDERED', 'RECEIVED']);

    if (location_ids && location_ids.length > 0) {
      query = query.in('location_id', location_ids);
    }

    if (manufacturer_ids && manufacturer_ids.length > 0) {
      query = query.in('manufacturer_id', manufacturer_ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching order efficiency:', error);
      throw new Error(error.message);
    }

    // Group by PO number + manufacturer + location to analyze actual orders
    const orderMap = new Map<string, {
      manufacturer_id: string;
      manufacturer_name: string;
      location_id: string;
      location_name: string;
      location_short_code?: string;
      po_number: string;
      ordered_date: string;
      frame_count: number;
      min_order_qty: number | null;
      min_order_amt: number | null;
      free_ship_amt: number | null;
    }>();

    (data || []).forEach((item: FrameReplenishmentQueryResult & {
      manufacturer?: { min_order_qty?: number | null; min_order_amt?: number | null; free_ship_amt?: number | null };
    }) => {
      const key = `${item.po_number}-${item.manufacturer_id}-${item.location_id}`;
      
      if (!orderMap.has(key)) {
        orderMap.set(key, {
          manufacturer_id: item.manufacturer_id,
          manufacturer_name: item.manufacturer?.name || 'Unknown',
          location_id: item.location_id,
          location_name: item.location?.name || 'Unknown',
          location_short_code: item.location?.short_code,
          po_number: item.po_number || '',
          ordered_date: item.ordered_date || '',
          frame_count: 0,
          min_order_qty: item.manufacturer?.min_order_qty || null,
          min_order_amt: item.manufacturer?.min_order_amt || null,
          free_ship_amt: item.manufacturer?.free_ship_amt || null,
        });
      }

      const order = orderMap.get(key)!;
      order.frame_count++;
    });

    // Calculate efficiency metrics
    const results: OrderEfficiencyData[] = Array.from(orderMap.values()).map((order) => {
      const meetsMinQty = order.min_order_qty === null || order.frame_count >= order.min_order_qty;
      // Note: We don't have order amount data, so can't calculate meets_min_amt or meets_free_ship
      const meetsMinAmt = null; // Would require order amount tracking
      const meetsFreeShip = null; // Would require order amount tracking

      let status: 'OPTIMAL' | 'MEETS_MIN' | 'BELOW_MIN';
      if (order.min_order_qty === null) {
        status = 'OPTIMAL'; // No minimum to meet
      } else if (meetsMinQty) {
        status = 'MEETS_MIN';
      } else {
        status = 'BELOW_MIN';
      }

      return {
        manufacturer_id: order.manufacturer_id,
        manufacturer_name: order.manufacturer_name,
        location_id: order.location_id,
        location_name: order.location_name,
        location_short_code: order.location_short_code,
        po_number: order.po_number,
        ordered_date: order.ordered_date,
        frame_count: order.frame_count,
        min_order_qty: order.min_order_qty,
        min_order_amt: order.min_order_amt,
        free_ship_amt: order.free_ship_amt,
        meets_min_qty: meetsMinQty,
        meets_min_amt: meetsMinAmt,
        meets_free_ship: meetsFreeShip,
        status,
      };
    });

    return results.sort((a, b) => {
      // Sort by status (BELOW_MIN first), then by manufacturer name
      const statusOrder = { BELOW_MIN: 0, MEETS_MIN: 1, OPTIMAL: 2 };
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;
      return a.manufacturer_name.localeCompare(b.manufacturer_name);
    });
  },
};

// ============================================================================
// Order Efficiency Report Types
// ============================================================================

export interface OrderEfficiencyData {
  manufacturer_id: string;
  manufacturer_name: string;
  location_id: string;
  location_name: string;
  location_short_code?: string;
  po_number: string;
  ordered_date: string;
  frame_count: number;
  min_order_qty: number | null;
  min_order_amt: number | null;
  free_ship_amt: number | null;
  meets_min_qty: boolean;
  meets_min_amt: boolean | null; // null if min_order_amt not set
  meets_free_ship: boolean | null; // null if free_ship_amt not set
  status: 'OPTIMAL' | 'MEETS_MIN' | 'BELOW_MIN';
}

export interface OrderEfficiencyFilters {
  date_from: string;
  date_to: string;
  location_ids?: string[];
  manufacturer_ids?: string[];
}
